import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  setDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { MessageSquare, Send, ArrowLeft, Clock } from 'lucide-react';

export default function SupportDetailPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();

  const [rootTicket, setRootTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user?.uid || !ticketId) {
      setLoading(false);
      return;
    }

    const rootRef = doc(db, 'users', user.uid, 'supportTickets', ticketId);

    const repliesQuery = query(
      collection(db, 'users', user.uid, 'supportTickets'),
      where('parentTicketId', '==', ticketId),
      orderBy('createdAt', 'asc'),
    );

    const unsubRoot = onSnapshot(
      rootRef,
      snapshot => {
        if (snapshot.exists()) {
          setRootTicket({ id: snapshot.id, ...snapshot.data() });
        }
        setLoading(false);
      },
      error => {
        console.error('Error loading root support ticket:', error);
        setLoading(false);
      },
    );

    const unsubReplies = onSnapshot(
      repliesQuery,
      snapshot => {
        const items =
          snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })) ||
          [];
        setReplies(items);
      },
      error => {
        console.error('Error loading support ticket replies:', error);
      },
    );

    return () => {
      unsubRoot();
      unsubReplies();
    };
  }, [user?.uid, ticketId]);

  const formatDate = date => {
    if (!date) return '';
    try {
      const dateObj = date?.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleString();
    } catch (e) {
      return '';
    }
  };

  const allTickets = [
    ...(rootTicket ? [rootTicket] : []),
    ...replies.slice(),
  ].sort((a, b) => {
    const aTime = a.createdAt?.toDate
      ? a.createdAt.toDate().getTime()
      : 0;
    const bTime = b.createdAt?.toDate
      ? b.createdAt.toDate().getTime()
      : 0;
    return aTime - bTime;
  });

  const messages = [];
  allTickets.forEach(ticketDoc => {
    // user message
    if (ticketDoc.message) {
      messages.push({
        id: `${ticketDoc.id}-user`,
        role: 'user',
        text: ticketDoc.message,
        createdAt: ticketDoc.createdAt,
      });
    }
    // support reply as separate message if exists
    if (ticketDoc.adminReply) {
      messages.push({
        id: `${ticketDoc.id}-support`,
        role: 'support',
        text: ticketDoc.adminReply,
        createdAt: ticketDoc.answeredAt || ticketDoc.createdAt,
      });
    }
  });

  messages.sort((a, b) => {
    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return aTime - bTime;
  });

  const handleSendReply = async () => {
    if (!user?.uid || !rootTicket) return;
    if (!reply.trim()) return;

    try {
      setSending(true);

      const subject =
        rootTicket.subject &&
        rootTicket.subject.toLowerCase().startsWith('re:')
          ? rootTicket.subject
          : `Re: ${rootTicket.subject || ''}`.trim();

      const payload = {
        userId: user.uid,
        userEmail: user.email || null,
        subject,
        message: reply.trim(),
        status: 'open',
        parentTicketId: rootTicket.id,
        createdAt: serverTimestamp(),
      };

      // Root collection for admin panel
      const ticketRef = await addDoc(collection(db, 'supportTickets'), payload);

      // Mirror under user document for user view with same ID
      await setDoc(
        doc(db, 'users', user.uid, 'supportTickets', ticketRef.id),
        payload,
      );

      setReply('');
      alert(
        t('profile.supportSent', {
          defaultValue:
            'Your message has been sent. We will get back to you soon.',
        }),
      );
    } catch (error) {
      console.error('Error sending follow-up support message:', error);
      alert(
        t('profile.supportFailed', {
          defaultValue: 'Failed to send support message. Please try again.',
        }),
      );
    } finally {
      setSending(false);
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-slate-100 mb-4">
              {t('profile.supportTitle') || 'Support'}
            </h1>
            <p className="text-gray-600 dark:text-slate-300 mb-6">
              {t('profile.supportLoginRequired', {
                defaultValue: 'You need to be logged in to contact support.',
              })}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('incomingRequests.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const createdAtLabel = formatDate(rootTicket?.createdAt);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <button
          onClick={() => navigate('/profile/support')}
          className="mb-6 flex items-center gap-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">
            {t('common.back') || 'Back to Support'}
          </span>
        </button>

        {loading ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8 text-center">
            <Clock className="h-8 w-8 mx-auto mb-4 text-gray-400 animate-spin" />
            <p className="text-gray-500 dark:text-slate-400">
              {t('common.loading') || 'Loading...'}
            </p>
          </div>
        ) : !rootTicket ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8 text-center">
            <p className="text-gray-600 dark:text-slate-400">
              {t('profile.supportDetailNotFound', {
                defaultValue: 'This support request could not be found.',
              })}
            </p>
          </div>
        ) : (
          <div className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 overflow-hidden">
            {/* Header */}
            <div className="p-8 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="h-6 w-6 text-yellow-500" />
                <h1 className="text-2xl font-black text-gray-900 dark:text-white">
                  {rootTicket.subject || '-'}
                </h1>
              </div>
              {createdAtLabel && (
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {createdAtLabel}
                </p>
              )}
            </div>

            {/* Messages */}
            <div className="p-8 space-y-4 max-h-[600px] overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-slate-400">
                    {t('profile.supportPendingReply', {
                      defaultValue:
                        'Our team has received your message and will reply soon.',
                    })}
                  </p>
                </div>
              ) : (
                messages.map(msg =>
                  msg.role === 'user' ? (
                    <div
                      key={msg.id}
                      className="flex justify-start"
                    >
                      <div className="max-w-[80%] rounded-2xl bg-gray-100 dark:bg-slate-800 p-4">
                        <p className="text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                          {t('profile.user') || 'You'}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                          {msg.text}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={msg.id}
                      className="flex justify-end"
                    >
                      <div className="max-w-[80%] rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
                        <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                          {t('profile.supportReplyLabel', {
                            defaultValue: 'Support reply',
                          })}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                          {msg.text}
                        </p>
                      </div>
                    </div>
                  ),
                )
              )}
            </div>

            {/* Reply Section */}
            <div className="p-8 border-t border-gray-200 dark:border-slate-700">
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {t('profile.supportFollowupPlaceholder', {
                  defaultValue:
                    'Write a follow-up message about this issue',
                })}
              </label>
              <div className="flex gap-3">
                <textarea
                  rows={3}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  disabled={sending}
                  placeholder={
                    t('profile.supportFollowupPlaceholder', {
                      defaultValue:
                        'Write a follow-up message about this issue',
                    }) || ''
                  }
                  className="flex-1 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 resize-none"
                />
                <button
                  onClick={handleSendReply}
                  disabled={sending || !reply.trim()}
                  className="rounded-xl bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-black font-bold px-6 py-3 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 self-end"
                >
                  {sending ? (
                    <Clock className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

